import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';

const TOUR_SEEN_KEY = 'kelvix_tour_seen_v1';

interface TourContextType {
  isTourActive: boolean;
  startTour: () => void;
  endTour: () => void;
  hasSeenTour: boolean;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within TourProvider');
  }
  return context;
};

interface TourProviderProps {
  children: ReactNode;
}

export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [isTourActive, setIsTourActive] = useState(false);
  const [hasSeenTour, setHasSeenTour] = useState(() => {
    return localStorage.getItem(TOUR_SEEN_KEY) === 'true';
  });

  const startTour = useCallback(() => {
    setIsTourActive(true);
  }, []);

  const endTour = useCallback(() => {
    setIsTourActive(false);
    setHasSeenTour(true);
    localStorage.setItem(TOUR_SEEN_KEY, 'true');
  }, []);

  // Auto-start tour for first-time users on dashboard
  useEffect(() => {
    if (user && !hasSeenTour && location.pathname === '/' && !isTourActive) {
      const timer = setTimeout(() => {
        setIsTourActive(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [user, hasSeenTour, location.pathname, isTourActive]);

  return (
    <TourContext.Provider value={{ isTourActive, startTour, endTour, hasSeenTour }}>
      {children}
    </TourContext.Provider>
  );
};
